































































































































import Vue from 'vue';
import { ssnPattern } from '@/validators';
import { Donor } from '@/interfaces/donation/donor';
import { mask } from 'vue-the-mask';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { getFromSpar } from '@/api/mainApi';
import { RequestSession } from '@/interfaces/requestSession';
import ConfirmFieldComponent from '@/components/form/ConfirmFieldComponent.vue';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import StepperFlowMixin from '@/mixins/donationStepperFlowMixin';
import ValidationMixin from '@/mixins/validationMixin';
import LabelMixin from '@/mixins/labelMixin';

export default Vue.extend({
  components: {
    ConfirmFieldComponent,
  },
  directives: {
    mask,
  },
  mixins: [StepperFlowMixin, ValidationMixin, LabelMixin],
  props: {
    value: {
      type: Object as () => Donor,
    },
    manual: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    pep: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      ssnLoading: false,
      ssnMask: this.$t('generic.masks.ssn') as string,
      ssnValid: false,
      showInputFields: false,
    };
  },
  computed: {
    steps(): typeof stepV1 | typeof stepV2 {
      return this.getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    },
    socialSecurityNumber() {
      return this.donor.socialSecurityNumber;
    },
    currentStep() {
      return this.$store.state.currentStep;
    },
    requestSession(): RequestSession {
      return this.$store.state.requestSession;
    },
    isDirectPaymentAvailable() {
      return (
        this.requestSession.organizationGroup &&
        this.requestSession.organizationGroup.swedbankPaySettings &&
        this.requestSession.organizationGroup.swedbankPaySettings.id
      );
    },
    optionalDonorPersonalIdentityNumber(): boolean {
      return !!this.requestSession?.optionalDonorPersonalIdentityNumber;
    },
    allowManualPersonalIdentityNumberInput(): boolean {
      return this.isDirectPaymentAvailable || this.optionalDonorPersonalIdentityNumber;
    },
    // If manual input is allowed and the donor has not entered a ssn, do not require the other fields
    mandatoryPersonalInformationInputFields(): boolean {
      return !this.optionalDonorPersonalIdentityNumber;
    },
    personalIdentityFieldRules(): Array<any> {
      return this.optionalDonorPersonalIdentityNumber ? [this.rules.pidEmpty] : [this.rules.pid];
    },
    personalInformationInputFieldRules(): Array<any> {
      return this.mandatoryPersonalInformationInputFields ? [this.rules.required] : [];
    },
    showPep() {
      // 2023-01-03 | PEP is no longer required so will always be hidden
      return false;
    },
    donor: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('update:value', val);
      },
    },
    showError: {
      get() {
        return this.error && !this.manualInput;
      },
      set(val) {
        this.$emit('update:error', val);
      },
    },
    manualInput: {
      get() {
        return this.manual;
      },
      set(val) {
        this.$emit('update:manual', val);
      },
    },
    donorEmail: {
      get() {
        return this.donor.email;
      },
      set(newValue) {
        this.donor.email = newValue;
      },
    },
  },
  watch: {
    currentStep(val) {
      if (val == this.steps.DONOR && !this.showInputFields && !this.manualInput) {
        this.$refs.ssn.focus();
      }
    },
    manualInput(val) {
      if (!!val) {
        // if manualInput is toggled we empty all the fields
        this.donor = {
          address: {
            address1: '',
            address2: '',
            address3: '',
            city: '',
            country: '',
            postcode: '',
          },
          email: '',
          firstname: '',
          surname: '',
          socialSecurityNumber: '',
          token: '',
        } as Donor;
      }
    },
    async socialSecurityNumber(newValue, oldValue) {
      let newValueExclHypen = newValue.replace(/-/g, '');
      let oldValueExclHypen = oldValue.replace(/-/g, '');
      if (newValueExclHypen !== oldValueExclHypen || this.showError) {
        this.showError = false;

        if (ssnPattern.test(newValue)) {
          this.ssnLoading = true;

          let formattedSsn: string = newValue.replace('-', '');
          if (this.currentStep == this.steps.DONOR) {
            getFromSpar(formattedSsn)
              .then(data => {
                data.socialSecurityNumber = newValue;
                this.donor = data;

                if (!this.isDirectPaymentAvailable && data.givenName && data.givenName.length > 0) {
                  this.$nextTick(() => {
                    // must be nextTick so we update data after its been assigned
                    this.donor.firstname = data.givenName;
                  });
                }

                this.showInputFields = true;
                this.$refs.email.focus();
              })
              .catch(error => {
                this.showError = true;
              })
              .finally(() => {
                this.ssnLoading = false;
              });
          }
        } else if (this.showInputFields) {
          this.showInputFields = false;
        }
      }
    },
  },
});
